/**
 * ╔══════════════════════════════════════════════════════════════╗
 *                         OPSecours™
 *              Emergency & Rescue Simulation Experience
 * ╠══════════════════════════════════════════════════════════════╣
 *   Developed by : Team OpSecours
 *   Technology   : TypeScript / TSX Web Game Engine
 *   Build Status : Active Development
 *
 *   © 2024 / 2026 OpSecours - Bondi51 & Team OpSecours
 *   All Rights Reserved.
 *
 *   Confidential project. Unauthorized distribution prohibited.
 * ╚══════════════════════════════════════════════════════════════╝
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from '../core/store/store';
import { LanguageProvider } from '../shared/contexts/LanguageContext';
import ProtectedRoute from '../shared/components/ProtectedRoute';
import NavigationLoader from '../shared/components/NavigationLoader';
import SessionRestorer from '../shared/components/SessionRestorer';

// Pages publiques (pré-connexion)
import Home from '../features/auth/pages/Home';
import HomeDesktop from '../features/auth/pages/HomeDesktop';
import ResetPassword from '../features/auth/pages/ResetPassword';
import ValidateAccount from '../features/auth/pages/ValidateAccount';
import { isElectron } from '../shared/utils/isElectron';
import { getRouterBasename } from '../core/config/api';

// Pages légales
import MentionsLegales from '../features/legal/pages/MentionsLegales';
import ConditionsUtilisation from '../features/legal/pages/ConditionsUtilisation';
import PolitiqueConfidentialite from '../features/legal/pages/PolitiqueConfidentialite';

// Pages de support
import Aide from '../features/support/pages/Aide';
import FAQ from '../features/support/pages/FAQ';
import Contact from '../features/support/pages/Contact';
import Changelog from '../features/support/pages/Changelog';

// Pages du jeu (post-connexion)
import Dashboard from '../features/game/dashboard/Dashboard';
import AlliancePage from '../features/game/dashboard/pages/AlliancePage';
import Map from '../features/game/Map';
import UsersManagement from '../features/panel/pages/UsersManagement';
import CmManagement from '../features/panel/pages/CmManagement';
import IndividualRoleManagement from '../features/panel/pages/IndividualRoleManagement';
import BackupManagement from '../features/panel/pages/BackupManagement';
import SystemConfiguration from '../features/panel/pages/SystemConfiguration';
import SecurityLogs from '../features/panel/pages/SecurityLogs';
import SystemMaintenance from '../features/panel/pages/SystemMaintenance';
import InterventionsCreate from '../features/panel/pages/InterventionsCreate';
import InterventionsManage from '../features/panel/pages/InterventionsManage';
import CtaCategoriesMissionManagement from '../features/panel/pages/CtaCategoriesMissionManagement';
import PanelSupportTeamPage from '../features/panel/pages/TeamSupport';
import PanelLayout from '../features/panel/layout/PanelLayout';
import PanelDashboardV1 from '../features/panel/pages/v1/PanelDashboardV1';
import PanelUsersRolesV1 from '../features/panel/pages/v1/PanelUsersRolesV1';
import PanelCtaV1 from '../features/panel/pages/v1/PanelCtaV1';
import PanelSecurityV1 from '../features/panel/pages/v1/PanelSecurityV1';
import PanelScenarioCreateV1 from '../features/panel/pages/v1/PanelScenarioCreateV1';
import PanelModerationV1 from '../features/panel/pages/v1/PanelModerationV1';
import PanelModerationManageV1 from '../features/panel/pages/v1/PanelModerationManageV1';
import PanelCmHubV1 from '../features/panel/pages/v1/PanelCmHubV1';
import PanelAdminHubV1 from '../features/panel/pages/v1/PanelAdminHubV1';
import PanelModoHubV1 from '../features/panel/pages/v1/PanelModoHubV1';
import PanelReferentHubV1 from '../features/panel/pages/v1/PanelReferentHubV1';
import PanelScenaristeHubV1 from '../features/panel/pages/v1/PanelScenaristeHubV1';
import PanelPlayersPremiumV1 from '../features/panel/pages/v1/PanelPlayersPremiumV1';
import PanelInterventionsManageEmbedV1 from '../features/panel/pages/v1/PanelInterventionsManageEmbedV1';
import PanelUserRolesEmbedV1 from '../features/panel/pages/v1/PanelUserRolesEmbedV1';
import PanelRoleDefaultAccessEmbedV1 from '../features/panel/pages/v1/PanelRoleDefaultAccessEmbedV1';
import PanelSectionGuard from '../features/panel/components/PanelSectionGuard';
import PanelTeamOnlyGuard from '../features/panel/components/PanelTeamOnlyGuard';
import {
  canPanelAdminTier,
  canPanelManageInterventions,
  canPanelManagePremium,
  canPanelModeratePlayers,
  canPanelSystemCm,
  canPanelViewInterventionsCta,
} from '../features/panel/panelAccess/panelRoleAccess';
import { PanelAccessBoundary } from '../features/panel/context/PanelAccessContext';

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <LanguageProvider>
        <Router
          basename={getRouterBasename()}
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <SessionRestorer />
          <NavigationLoader />
          <PanelAccessBoundary>
          <Routes>
            {/* Pages publiques */}
            <Route path="/" element={isElectron() ? <HomeDesktop /> : <Home />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/validate" element={<ValidateAccount />} />

            {/* Pages légales */}
            <Route path="/legal/mentions-legales" element={<MentionsLegales />} />
            <Route path="/legal/conditions-utilisation" element={<ConditionsUtilisation />} />
            <Route path="/legal/politique-confidentialite" element={<PolitiqueConfidentialite />} />
            
            {/* Pages de support */}
            <Route path="/support/aide" element={<Aide />} />
            <Route path="/support/faq" element={<FAQ />} />
            <Route path="/support/contact" element={<Contact />} />
            <Route path="/support/changelog" element={<Changelog />} />
            
            {/* Pages protégées (jeu) */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/alliance"
              element={
                <ProtectedRoute>
                  <AlliancePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/game/map"
              element={
                <ProtectedRoute>
                  <Map />
                </ProtectedRoute>
              }
            />
            <Route
              path="/panel"
              element={
                <ProtectedRoute>
                  <PanelLayout />
                </ProtectedRoute>
              }
            >
              <Route element={<PanelTeamOnlyGuard />}>
              <Route index element={<PanelDashboardV1 />} />
              <Route path="dashboard" element={<PanelDashboardV1 />} />
              <Route
                path="users-roles"
                element={
                  <PanelSectionGuard section="salon_joueurs" defaultAccess={canPanelModeratePlayers}>
                    <PanelUsersRolesV1 />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="players/premium"
                element={
                  <PanelSectionGuard section="player_premium" defaultAccess={canPanelManagePremium}>
                    <PanelPlayersPremiumV1 />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="moderation"
                element={
                  <PanelSectionGuard section="moderation" defaultAccess={canPanelModeratePlayers}>
                    <PanelModerationV1 />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="moderation/manage/:userId"
                element={
                  <PanelSectionGuard section="moderation" defaultAccess={canPanelModeratePlayers}>
                    <PanelModerationManageV1 />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="cta"
                element={
                  <PanelSectionGuard section="interventions" defaultAccess={canPanelViewInterventionsCta}>
                    <PanelCtaV1 />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="interventions/create"
                element={
                  <PanelSectionGuard section="interventions_manage" defaultAccess={canPanelManageInterventions}>
                    <PanelScenarioCreateV1 />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="interventions/manage"
                element={
                  <PanelSectionGuard section="interventions_manage" defaultAccess={canPanelManageInterventions}>
                    <PanelInterventionsManageEmbedV1 />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="scenarios"
                element={
                  <PanelSectionGuard section="interventions_manage" defaultAccess={canPanelManageInterventions}>
                    <PanelScenarioCreateV1 />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="security"
                element={
                  <PanelSectionGuard section="salon_security" defaultAccess={canPanelModeratePlayers}>
                    <PanelSecurityV1 />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="config"
                element={
                  <PanelSectionGuard section="system" defaultAccess={canPanelSystemCm}>
                    <SystemConfiguration embedded />
                  </PanelSectionGuard>
                }
              />
              <Route path="cm" element={<Outlet />}>
                <Route
                  index
                  element={
                    <PanelSectionGuard section="salon_cm" rolesAnyOf={[1]} defaultAccess={() => true}>
                      <PanelCmHubV1 />
                    </PanelSectionGuard>
                  }
                />
                <Route
                  path="maintenance"
                  element={
                    <PanelSectionGuard section="salon_cm" rolesAnyOf={[1]} defaultAccess={() => true}>
                      <SystemMaintenance embedded />
                    </PanelSectionGuard>
                  }
                />
                <Route
                  path="referent"
                  element={
                    <PanelSectionGuard section="salon_cm" rolesAnyOf={[1]} defaultAccess={canPanelSystemCm}>
                      <CmManagement embedded />
                    </PanelSectionGuard>
                  }
                />
              </Route>
              <Route
                path="admin"
                element={
                  <PanelSectionGuard
                    section="salon_admin"
                    defaultAccess={(r) => r === 1 || r === 2}
                  >
                    <PanelAdminHubV1 />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="modo"
                element={
                  <PanelSectionGuard
                    section="salon_modo"
                    defaultAccess={(r) => r === 1 || r === 3}
                  >
                    <PanelModoHubV1 />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="scenariste"
                element={
                  <PanelSectionGuard
                    section="salon_scenariste"
                    defaultAccess={(r) => r === 1 || r === 4}
                  >
                    <PanelScenaristeHubV1 />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="ref"
                element={
                  <PanelSectionGuard
                    section="salon_ref"
                    defaultAccess={(r) => r === 4 || r === 5}
                  >
                    <PanelReferentHubV1 />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="users/roles"
                element={
                  <PanelSectionGuard
                    section="admin"
                    defaultAccess={canPanelAdminTier}
                    requireReferentFunction="Référent Admin"
                    referentBypassRoles={[2]}
                  >
                    <PanelUserRolesEmbedV1 />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="roles/default-access"
                element={
                  <PanelSectionGuard section="admin" defaultAccess={canPanelAdminTier}>
                    <PanelRoleDefaultAccessEmbedV1 />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="roles/individual"
                element={
                  <PanelSectionGuard section="roles" rolesAnyOf={[1]} defaultAccess={canPanelSystemCm}>
                    <IndividualRoleManagement />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="users"
                element={
                  <PanelSectionGuard section="admin" defaultAccess={canPanelAdminTier}>
                    <UsersManagement />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="system/backup"
                element={
                  <PanelSectionGuard section="system" defaultAccess={canPanelSystemCm}>
                    <BackupManagement />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="system/security-logs"
                element={
                  <PanelSectionGuard section="salon_security" defaultAccess={canPanelAdminTier}>
                    <SecurityLogs />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="cta/categories-mission"
                element={
                  <PanelSectionGuard section="interventions_manage" defaultAccess={canPanelManageInterventions}>
                    <CtaCategoriesMissionManagement />
                  </PanelSectionGuard>
                }
              />
              <Route
                path="support/team"
                element={
                  <PanelSectionGuard section="salon_ref" defaultAccess={(r) => r === 4 || r === 5}>
                    <PanelSupportTeamPage />
                  </PanelSectionGuard>
                }
              />
              </Route>
            </Route>
            <Route path="/panel/legacy" element={<Navigate to="/panel/dashboard" replace />} />
            <Route
              path="/panel/system/configuration"
              element={<Navigate to="/panel/config" replace />}
            />
            <Route
              path="/panel/system/maintenance"
              element={
                <ProtectedRoute>
                  <Navigate to="/panel/cm/maintenance" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/panel/interventions/create"
              element={
                <ProtectedRoute>
                  <PanelSectionGuard section="interventions_manage" defaultAccess={canPanelManageInterventions}>
                    <InterventionsCreate />
                  </PanelSectionGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/panel/interventions/edit/:id"
              element={
                <ProtectedRoute>
                  <PanelSectionGuard section="interventions_manage" defaultAccess={canPanelManageInterventions}>
                    <InterventionsCreate />
                  </PanelSectionGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/panel/interventions/manage"
              element={
                <ProtectedRoute>
                  <PanelSectionGuard section="interventions_manage" defaultAccess={canPanelManageInterventions}>
                    <InterventionsManage />
                  </PanelSectionGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/panel/v1/*"
              element={
                <ProtectedRoute>
                  <Navigate to="/panel" replace />
                </ProtectedRoute>
              }
            />
          </Routes>
          </PanelAccessBoundary>
        </Router>
      </LanguageProvider>
    </Provider>
  );
};

export default App;

